import type { FormEvent } from 'react'
import { useState } from 'react'

interface WaitlistFormData {
  name: string
  contact: string
}

interface WaitlistFormProps {
  onSuccess?: (data: WaitlistFormData) => void
  onError?: (error: Error) => void
  'data-testid'?: string
}

export function WaitlistForm({
  onSuccess,
  onError,
  'data-testid': testId
}: WaitlistFormProps) {
  const [formData, setFormData] = useState<WaitlistFormData>({
    name: '',
    contact: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, contactType: 'phone' })
      })

      if (!response.ok) {
        throw new Error('Failed to join waitlist')
      }

      setSubmitStatus('success')
      onSuccess?.(formData)
    } catch (error) {
      setSubmitStatus('error')
      const errorMsg = error instanceof Error ? error.message : 'Something went wrong'
      setErrorMessage(errorMsg)
      onError?.(error instanceof Error ? error : new Error(errorMsg))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof WaitlistFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  if (submitStatus === 'success') {
    return (
      <div style={{ textAlign: 'center' }} data-testid={`${testId}-success`}>
        <span
          style={{
            width: '56px',
            height: '56px',
            display: 'inline-grid',
            placeItems: 'center',
            background: 'var(--color-accent)',
            color: 'var(--color-bg)',
            marginBottom: '14px'
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5"/>
          </svg>
        </span>
        <h2 style={{ margin: '0 0 8px' }}>You're on the list.</h2>
        <p style={{ fontSize: '15px', color: 'var(--color-neutral-800)', margin: '0 0 20px' }}>
          We'll send you a message the moment TALLY is ready for your area. No spam — just the one message.
        </p>
        <a
          href="/contact"
          className="btn btn-primary"
          style={{ minHeight: '48px', textDecoration: 'none' }}
        >
          Actually, book a demo now
        </a>
        <div style={{ marginTop: '16px' }}>
          <a href="/" style={{ fontSize: '13px', color: 'var(--color-accent)', textDecoration: 'none' }}>
            &larr; Back to home
          </a>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} data-testid={testId}>
      {submitStatus === 'error' && (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3)',
            marginBottom: 'var(--space-3)',
            border: '1px solid #f5c6c6',
            background: '#fef2f2',
            color: '#991b1b',
            fontSize: '14px'
          }}
        >
          {errorMessage || 'Failed to join waitlist. Please try again.'}
        </div>
      )}

      <div className="field" style={{ marginBottom: 'var(--space-3)' }}>
        <label>Your name</label>
        <input
          className="input"
          placeholder="Ama Owusu"
          style={{ minHeight: '46px' }}
          required
          value={formData.name}
          onChange={handleChange('name')}
          disabled={isSubmitting}
        />
      </div>
      <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
        <label>Phone or email</label>
        <input
          className="input"
          placeholder="024 000 0000"
          style={{ minHeight: '46px' }}
          required
          value={formData.contact}
          onChange={handleChange('contact')}
          disabled={isSubmitting}
        />
      </div>
      <button
        type="submit"
        className="btn btn-primary btn-block"
        style={{ minHeight: '52px', fontSize: '16px' }}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Adding...' : 'Join the waitlist'}
      </button>
      <p className="text-muted" style={{ fontSize: '12px', margin: '12px 0 0', textAlign: 'center' }}>
        One message when we're ready. Nothing else.
      </p>
    </form>
  )
}
